<script lang="ts">
const __DsBlock = {
	icon: 'lucide:text-cursor-input',
	name: '$t:blocks.input_number.name',
	description: '$t:blocks.input_number.description',
	type: 'input',
	category: 'input',
	fieldBinding: {
		supportedTypes: ({ primitiveType }) => {
			if (isPrimitiveTypeDecimal(primitiveType)) return [];

			const types = ['number'];
			if (isPrimitiveTypeInteger(primitiveType)) types.push('identifier');
			return types;
		},
		recommendedDisplays: ['$builtin:DisplayFormattedValue'],
	},
	options: [
		{
			name: 'modelValue',
			model: true,
		},
		{
			name: 'min',
		},
		{
			name: 'max',
		},
		{
			name: 'step',
		},
		{
			name: 'stepSnapping',
		},
		{
			name: 'placeholder',
		},
		{
			name: 'color',
			type: 'enum',
			values: BLOCK_COLOR_OPTIONS,
		},
		{
			name: 'variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'block',
		},
		{
			name: 'disabled',
		},
		{
			name: 'readonly',
		},
		{
			name: 'disabledNotice',
		},
	],
}
</script>
<script lang="ts" setup>
import type { InputNumberProps } from '#layers/ui';
import type { InputBlockProps } from '~/types/input-block';
import { reactivePick } from '@vueuse/core';
import { defineBlock } from '#layers/block-engine';
import { BLOCK_COLOR_OPTIONS, BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { isPrimitiveTypeDecimal, isPrimitiveTypeInteger } from '#layers/data-model';
import { useDisabledNotice } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';

interface Props extends
	InputBlockProps,
	Pick<InputNumberProps, 'placeholder' | 'color' | 'variant' | 'size' | 'min' | 'max' | 'step' | 'stepSnapping'> {
	/**
	 * Render the input full width
	 */
	block?: boolean;
}

const props = withDefaults(defineProps<Props>(), { block: true });
const model = defineModel<number>();

;

const forwarded = reactivePick(props, 'placeholder', 'color', 'variant', 'size', 'min', 'max', 'step', 'stepSnapping', 'readonly');
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DInputNumber
			v-bind="forwarded"
			v-model="modelValue"
			:disabled
			:aria-readonly="readonly"
			class="min-w-0"
			:class="{ 'w-full flex-1': block }"
		/>
	</DTooltip>
</template>
